import { Collapse, makeStyles, Paper, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import IrisPickBadge from 'src/components/IrisPickBadge';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { useCarRecommendation } from 'src/hooks/useRecommendations';
import { useUserPermissions, UserPermissions } from 'src/hooks/useUserPermissions';
import { getRecommendationReasonText } from 'src/modules/labels';

import { DetailCarDTO, Recommended } from '../modules/generated/api';

type CarDetailRecommendationProps = {
  car: DetailCarDTO;
};

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.intelligence.main}`,
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(1, 1.5),
    border: 'none',
    background: 'none',
    font: 'inherit',
    cursor: 'pointer',
  },
  moreInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  chevron: {
    transition: theme.transitions.create('transform'),
  },
  chevronExpanded: {
    transform: 'rotate(180deg)',
  },
  details: {
    padding: theme.spacing(0, 1.5, 1.5),
  },
}));

const CarDetailRecommendation = ({ car }: CarDetailRecommendationProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const irisRecommendationEnabled = useFeatureFlags('irisRecommendation');
  const irisRecommendationAllowed = useUserPermissions(UserPermissions.IRIS_RECOMMENDATION);
  const [expanded, setExpanded] = useState(false);

  const enabled = irisRecommendationEnabled && irisRecommendationAllowed && car.recommended === Recommended.Recommended;
  const { data: apiData, isLoading } = useCarRecommendation(
    enabled ? car.source : undefined,
    enabled ? car.offerId : undefined,
  );

  if (!enabled) {
    return null;
  }

  const summary = getRecommendationReasonText(apiData?.reason) ?? t('carDetailContent.recommendation.description');

  return (
    <Paper elevation={3} className={classes.paper}>
      <button type="button" className={classes.toggle} aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
        <IrisPickBadge />
        <Typography variant="body2" component="span" className={classes.moreInfo}>
          {!expanded && t('recommendationsPage.moreInfo')}
          <ExpandMoreIcon fontSize="small" className={clsx(classes.chevron, expanded && classes.chevronExpanded)} />
        </Typography>
      </button>
      <Collapse in={expanded}>
        <div className={classes.details}>
          {isLoading ? <Skeleton variant="text" width="80%" /> : <Typography variant="body2">{summary}</Typography>}
        </div>
      </Collapse>
    </Paper>
  );
};

export default CarDetailRecommendation;
